// Mukti Hospital ERP - NICU Offline Outbox (Phase: NICU offline write)
// NEW FILE: src/services/nicuOutbox.js
//
// Queues NICU admissions and bills created while offline, so the sync engine
// can push them to the server once the network returns.
//
// Storage: reuses the shared pathologyDb (IndexedDB "mukti-pathology-db"),
// specifically the `nicuOutbox` and `nicuLocal` stores added in version(2).
// It does NOT create its own Dexie instance, and it never touches the
// pathology stores (refCache, outbox, localBills).
//
// nicuOutbox entry shape (keyed by clientId):
// {
//   clientId: string,       // UUID, generated once per offline create
//   type: "admission" | "bill",
//   payload: object,        // exactly what the create endpoint expects (incl. clientId)
//   provisionalNo: string,  // e.g. "OFFLINE-<short>" shown until synced
//   status: "pending" | "syncing" | "failed",
//   error: string | null,   // last sync error (e.g. "Bed already occupied")
//   createdAt: string,      // ISO timestamp (drives sync order)
// }
//
// Every function is defensive: it never throws, so offline creation and the
// sync loop can't crash the UI. On any IndexedDB failure it logs and returns a
// safe empty value.

import pathologyDb from "./pathologyDb";

/**
 * Generate a UUID for a new offline entry. Uses crypto.randomUUID when
 * available (all modern browsers) with a small fallback.
 */
export const generateClientId = () => {
  try {
    if (typeof crypto !== "undefined" && crypto.randomUUID) {
      return crypto.randomUUID();
    }
  } catch (e) {
    /* fall through */
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

/**
 * Add an offline-created admission or bill to the outbox.
 * Returns the stored entry (with clientId + provisionalNo), or null on failure.
 */
export const addToNicuOutbox = async (type, payload, provisionalNo) => {
  try {
    const clientId = payload.clientId || generateClientId();
    const entry = {
      clientId,
      type,
      payload: { ...payload, clientId },
      provisionalNo: provisionalNo || `OFFLINE-${clientId.slice(0, 8)}`,
      status: "pending",
      error: null,
      createdAt: new Date().toISOString(),
    };
    await pathologyDb.nicuOutbox.put(entry);
    return entry;
  } catch (e) {
    console.warn("[NicuOutbox] addToNicuOutbox failed:", e);
    return null;
  }
};

/**
 * Read outbox entries, oldest first (so an admission created before its bill
 * syncs first). Optionally filter by type. Never throws.
 */
export const getNicuOutbox = async (typeFilter) => {
  try {
    let list = await pathologyDb.nicuOutbox.toArray();
    if (typeFilter) list = list.filter((e) => e.type === typeFilter);
    return list.sort(
      (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
    );
  } catch (e) {
    console.warn("[NicuOutbox] getNicuOutbox failed:", e);
    return [];
  }
};

/**
 * Count pending/failed entries (handy for a header badge). Never throws.
 */
export const getNicuOutboxCount = async () => {
  try {
    return await pathologyDb.nicuOutbox.count();
  } catch (e) {
    return 0;
  }
};

/**
 * Remove an entry once it has synced successfully. Never throws.
 */
export const removeFromNicuOutbox = async (clientId) => {
  try {
    await pathologyDb.nicuOutbox.delete(clientId);
    return true;
  } catch (e) {
    console.warn("[NicuOutbox] removeFromNicuOutbox failed:", e);
    return false;
  }
};

/**
 * Update an entry's status/error (e.g. mark "failed" with a bed-conflict
 * message so the user can retry with a different bed). Never throws.
 */
export const updateNicuOutboxStatus = async (clientId, status, error) => {
  try {
    const entry = await pathologyDb.nicuOutbox.get(clientId);
    if (!entry) return false;
    entry.status = status;
    entry.error = error || null;
    await pathologyDb.nicuOutbox.put(entry);
    return true;
  } catch (e) {
    console.warn("[NicuOutbox] updateNicuOutboxStatus failed:", e);
    return false;
  }
};

// ---- nicuLocal: local copy of offline records for list UIs ----------------

/**
 * Save a local copy of an offline-created record so it shows in list views
 * with a "Pending Sync" badge. Never throws.
 */
export const saveNicuLocal = async (record) => {
  try {
    if (!record || !record.clientId) return false;
    await pathologyDb.nicuLocal.put(record);
    return true;
  } catch (e) {
    console.warn("[NicuOutbox] saveNicuLocal failed:", e);
    return false;
  }
};

/**
 * Read local offline records (optionally by type), newest first. Never throws.
 */
export const getNicuLocal = async (typeFilter) => {
  try {
    let list = await pathologyDb.nicuLocal.toArray();
    if (typeFilter) list = list.filter((e) => e.type === typeFilter);
    return list.sort(
      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );
  } catch (e) {
    console.warn("[NicuOutbox] getNicuLocal failed:", e);
    return [];
  }
};

/**
 * Remove a local record once its real server record exists. Never throws.
 */
export const removeNicuLocal = async (clientId) => {
  try {
    await pathologyDb.nicuLocal.delete(clientId);
    return true;
  } catch (e) {
    console.warn("[NicuOutbox] removeNicuLocal failed:", e);
    return false;
  }
};
